import { useTranslation } from 'react-i18next'

/** LocalizedString: object, JSON string, or plain string */
export type LocalizedInput = Record<string, string> | string | null | undefined

const FALLBACK_LANG = 'ar'

/**
 * Normalize API/localized input to Record<lang, string> for visibility checks.
 * Handles LocalizedString object, JSON string, or plain string.
 */
export function normalizeLocalizedInput(val: LocalizedInput): Record<string, string> | null {
  if (val == null) return null
  if (typeof val === 'object' && !Array.isArray(val)) {
    const o = val as Record<string, string>
    if (typeof o[FALLBACK_LANG] === 'string' || Object.keys(o).some((k) => typeof o[k] === 'string'))
      return o
    return null
  }
  if (typeof val === 'string') {
    const s = val.trim()
    if (!s) return null
    if (s.startsWith('{')) {
      try {
        const parsed = JSON.parse(s) as Record<string, string>
        return typeof parsed === 'object' && parsed !== null ? parsed : null
      } catch {
        return { [FALLBACK_LANG]: s }
      }
    }
    return { [FALLBACK_LANG]: s }
  }
  return null
}

/**
 * Get value for a language from normalized record (case-insensitive key).
 */
function getValueForLang(rec: Record<string, string>, languageCode: string): string | undefined {
  const base = (languageCode || '').split('-')[0].toLowerCase()
  if (rec[base] !== undefined) return rec[base]
  const key = Object.keys(rec).find((k) => k.toLowerCase() === base)
  return key != null ? rec[key] : undefined
}

/**
 * Check if content has a PUBLISHED translation in the **selected** language only.
 * No fallback to other languages for visibility. Draft / missing = not available.
 * Keys are matched case-insensitively (e.g. EN vs en).
 */
export function isContentAvailableInLanguage(
  localizedContent: LocalizedInput,
  languageCode: string
): boolean {
  const rec = normalizeLocalizedInput(localizedContent)
  if (!rec) return false
  const value = getValueForLang(rec, languageCode)
  return (value?.trim().length ?? 0) > 0
}

/**
 * Get content value for current language with fallback
 */
export function getLocalizedContent(
  localizedContent: LocalizedInput,
  languageCode: string,
  fallbackLanguage: string = FALLBACK_LANG
): string {
  const rec = normalizeLocalizedInput(localizedContent)
  if (!rec) return ''
  const base = (languageCode || '').split('-')[0].toLowerCase() || fallbackLanguage
  if (rec[base]?.trim()) return rec[base]
  if (rec[fallbackLanguage]?.trim()) return rec[fallbackLanguage]
  const first = Object.values(rec).find((v) => typeof v === 'string' && v.trim())
  return (first as string) || ''
}

/**
 * Filter array of items by language availability
 */
export function filterByLanguageAvailability<T>(
  items: T[],
  checkFn: (item: T, langCode: string) => boolean,
  languageCode: string
): T[] {
  return items.filter((item) => checkFn(item, languageCode))
}

/**
 * Check if a section should be visible
 */
export function isSectionVisible(
  items: unknown[],
  checkFn: (item: unknown, langCode: string) => boolean,
  languageCode: string
): boolean {
  if (!items || items.length === 0) return false
  return items.some((item) => checkFn(item, languageCode))
}

/** Hero slide: visible if title has PUBLISHED translation in lang. */
export function isHeroSlideVisible(
  slide: { title: LocalizedInput; description?: LocalizedInput },
  lang: string
): boolean {
  return isContentAvailableInLanguage(slide.title, lang)
}

/** FAQ: visible only if both question AND answer have translation in lang. */
export function isFAQVisible(
  faq: { question: LocalizedInput; answer: LocalizedInput },
  lang: string
): boolean {
  return (
    isContentAvailableInLanguage(faq.question, lang) &&
    isContentAvailableInLanguage(faq.answer, lang)
  )
}

/** Story: visible if title (details) available in lang. */
export function isStoryVisible(story: { title: LocalizedInput }, lang: string): boolean {
  return isContentAvailableInLanguage(story.title, lang)
}

/** Book: visible if title (metadata) available in lang. */
export function isBookVisible(book: { title: LocalizedInput }, lang: string): boolean {
  return isContentAvailableInLanguage(book.title, lang)
}

/**
 * Hook to get current language code
 */
export function useCurrentLanguage(): string {
  const { i18n } = useTranslation()
  return i18n.language.split('-')[0] || FALLBACK_LANG
}
